import cn from 'clsx';
import {useRouter} from 'next/router';
import {useTranslation} from 'next-i18next';
import PropTypes from 'prop-types';
import {memo, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {ROLES_NAMES} from '@/src/app/constants/roles';
import {
  ITEMS_PER_PAGE,
  RESULT_DATA_TYPE,
  SEARCH_FILTERS,
} from '@/src/app/constants/search';
import {selectResults} from '@/src/app/store/search/searchSlice';
import {getAuthorizationToken} from '@/src/app/utils/authorizationToken';
import scrollTopSmoothly from '@/src/app/utils/scrollTopSmoothly';
import useIsMobile from '@/src/hooks/useIsMobile';
import getDataForVacancyItem from '@/src/jobPortal/allVacancies/styles/getDataForVacancyItem';
import ClinicPositionDesktopWidget from '@/src/profile/clinicTarget/components/ClinicPositionDesktopWidget';
import ClinicPositionMobileWidget from '@/src/profile/clinicTarget/components/ClinicPositionMobileWidget';
import PostCard from '@/src/Search/components/PostCard';

import {CourseCard, Pagination, UserCard} from '../../ui';
import EmptySearchResults from '../components/EmptySearchResults';
import css from '../styles/SearchResults.module.css';
import {getResultDataType} from '../utils';
import {useSearchFilters} from '../utils/useSearchFilters';

const SearchResults = ({
  onFollowToggleCallback,
  currentUserId,
  clinicData,
  employmentTypes,
  userType,
  onSuccessfulApplicationCallback,
}) => {
  const {query, locale} = useRouter();
  const {data, totalAmount} = useSelector(selectResults);
  // @ts-ignore
  const {setQueryFilters} = useSearchFilters();
  const {isMaxWidth1000, isMaxWidth750} = useIsMobile();
  const {t} = useTranslation('common');

  const isClinic = userType === ROLES_NAMES.CLINIC;

  const startPage = parseInt(
    // @ts-ignore
    query[SEARCH_FILTERS.OFFSET] / ITEMS_PER_PAGE[query.slug]
  );

  const handlePagination = ({selected}) => {
    // @ts-ignore
    const newOffset = (selected * ITEMS_PER_PAGE[query.slug]).toString();

    if (selected.toString() !== query[SEARCH_FILTERS.OFFSET]) {
      setQueryFilters({[SEARCH_FILTERS.OFFSET]: newOffset});
      scrollTopSmoothly();
    }
  };

  const isUserCardMobile = useMemo(
    () =>
      (getResultDataType(query.slug) === RESULT_DATA_TYPE.USER ||
        getResultDataType(query.slug) === RESULT_DATA_TYPE.CLINICS) &&
      isMaxWidth750,
    [isMaxWidth750, query?.slug]
  );

  const renderData = useMemo(() => {
    return data?.map((item) => {
      switch (getResultDataType(query.slug)) {
        case RESULT_DATA_TYPE.COURSE: {
          return <CourseCard {...item} key={item.id} />;
        }
        case RESULT_DATA_TYPE.USER:
        case RESULT_DATA_TYPE.FOLLOW:
        case RESULT_DATA_TYPE.CLINICS: {
          return (
            <UserCard
              {...item}
              key={item.id}
              onFollowToggleCallback={onFollowToggleCallback}
              currentUserId={isClinic ? clinicData.id : currentUserId}
              withOpenToJob={item?.isOpenForJob}
            />
          );
        }
        case RESULT_DATA_TYPE.POSTS: {
          return (
            <PostCard
              {...item}
              isWatchCourse={Boolean(getAuthorizationToken())}
              key={item.id}
            />
          );
        }
        case RESULT_DATA_TYPE.VACANCY: {
          if (isMaxWidth1000) {
            return (
              <ClinicPositionMobileWidget
                {...getDataForVacancyItem(
                  item,
                  item?.clinic ? item.clinic : clinicData,
                  employmentTypes,
                  locale,
                  t
                )}
                onSuccessfulApplicationCallback={
                  onSuccessfulApplicationCallback
                }
              />
            );
          }

          return (
            <ClinicPositionDesktopWidget
              {...getDataForVacancyItem(
                item,
                item?.clinic ? item.clinic : clinicData,
                employmentTypes,
                locale,
                t
              )}
              onSuccessfulApplicationCallback={onSuccessfulApplicationCallback}
            />
          );
        }
      }
    });
  }, [
    data,
    clinicData,
    employmentTypes,
    locale,
    isMaxWidth1000,
    onSuccessfulApplicationCallback,
  ]);

  if (!data) {
    return null;
  }

  return data?.length > 0 ? (
    <>
      <div
        className={cn({
          [css.resultList]:
            getResultDataType(query.slug) !== RESULT_DATA_TYPE.VACANCY &&
            !isUserCardMobile,
          [css.twoItemsPerRow]:
            (getResultDataType(query.slug) === RESULT_DATA_TYPE.USER ||
              getResultDataType(query.slug) === RESULT_DATA_TYPE.FOLLOW ||
              getResultDataType(query.slug) === RESULT_DATA_TYPE.CLINICS) &&
            !isUserCardMobile,
          [css.oneItemPerRow]:
            getResultDataType(query.slug) === RESULT_DATA_TYPE.VACANCY ||
            isUserCardMobile ||
            getResultDataType(query.slug) === RESULT_DATA_TYPE.POSTS,
          [css.oneItemPerRowDesktop]:
            getResultDataType(query.slug) === RESULT_DATA_TYPE.VACANCY &&
            !isMaxWidth1000,
        })}
      >
        {renderData}
      </div>
      <Pagination
        startPage={startPage}
        onPageChange={handlePagination}
        totalItems={totalAmount}
        // @ts-ignore
        limitPerPage={ITEMS_PER_PAGE[query.slug]}
      />
    </>
  ) : (
    <EmptySearchResults />
  );
};

SearchResults.propTypes = {
  onFollowToggleCallback: PropTypes.func,
  currentUserId: PropTypes.number,
  clinicData: PropTypes.object,
  employmentTypes: PropTypes.array,
  userType: PropTypes.string,
  onSuccessfulApplicationCallback: PropTypes.func,
};

SearchResults.defaultProps = {
  clinicData: {},
  employmentTypes: [],
  userType: '',
};

export default memo(SearchResults);
